import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { AdminRole } from '../auth/store';

/**
 * The permission catalogue, fetched from the API (`GET /admin/rbac/catalog`).
 *
 * It is deliberately NOT duplicated in this repo: the backend owns the list
 * that its guard enforces, so a permission added there shows up as a checkbox
 * here with no frontend release, and the panel can never offer a box that
 * grants nothing.
 */
export interface PermissionDef {
  key: string;
  label: string;
  kind: 'view' | 'action';
  hint?: string;
}

export interface PageDef {
  label: string;
  /** Admin-panel route, or null for an action-only group. */
  route: string | null;
  permissions: PermissionDef[];
}

export interface ModuleDef {
  key: string;
  label: string;
  pages: PageDef[];
}

export interface RbacCatalog {
  modules: ModuleDef[];
  roles: AdminRole[];
  /** What each built-in role grants before any per-admin ticks. */
  roleTemplates: Record<AdminRole, string[]>;
  /** Roles with an empty template — for these the boxes ARE the access. */
  grantOnlyRoles: AdminRole[];
}

/** The catalogue changes only on deploy, so it is cached hard. */
export function useRbacCatalog(enabled = true) {
  return useQuery({
    queryKey: ['admin', 'rbac', 'catalog'],
    queryFn: async () => (await api.get<RbacCatalog>('/admin/rbac/catalog')).data,
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
    enabled,
  });
}

/** The first permission of a page — the one that opens it. */
export const pageViewPermission = (page: PageDef): string | undefined =>
  page.permissions[0]?.key;
